import Cookies from 'js-cookie';

import { AUTH_COOKIES, COOKIES, OG_COOKIE, OKTA_COOKIES } from '../constants/cookies';
import { AUTH_HEADERS } from '../constants/headers';
import { AUTH0_CART_MERGE_COMPLETED_KEY } from '../constants/localStorage';
import { invalidateAuthSessionEpoch } from './authSessionEpoch';

const lastActiveCookieOptions = {
    sameSite: 'lax' as const,
    secure: true,
    path: '/',
};

export function setLastActiveCookie() {
    Cookies.set(AUTH_COOKIES.LAST_ACTIVE, Date.now().toString(), lastActiveCookieOptions);
    Cookies.remove(AUTH_COOKIES.AUTH0_REAUTH, { path: '/' });
}

/**
 * Ends the local Auth0 activity window: deletes `lastActive` (legacy logout
 * parity) and sets `auth0Reauth` so `isLastActiveExpired()` stays true.
 *
 * Deleting `lastActive` without `auth0Reauth` makes `isLastActiveExpired()`
 * return false, which lets `Auth0TokenSync` resurrect the access cookie while
 * the Auth0 SDK is still authenticated.
 */
export function markLastActiveExpired() {
    Cookies.remove(AUTH_COOKIES.LAST_ACTIVE, { path: '/' });
    Cookies.set(AUTH_COOKIES.AUTH0_REAUTH, 'true', lastActiveCookieOptions);
}

/** Cleared on Auth0 login callback so a fresh session is not treated as inactive. */
export function clearLastActiveCookie() {
    Cookies.remove(AUTH_COOKIES.LAST_ACTIVE, { path: '/' });
    Cookies.remove(AUTH_COOKIES.AUTH0_REAUTH, { path: '/' });
}

export function getAccessToken() {
    return Cookies.get(AUTH_COOKIES.ACCESS_TOKEN) ?? '';
}

export function clearAuth0AccessTokenMarker() {
    Cookies.remove(AUTH_COOKIES.IS_ACCESS_TOKEN_AUTH0, { path: '/' });
}

export function setAccessToken(accessToken: string) {
    Cookies.set(AUTH_COOKIES.ACCESS_TOKEN, accessToken, {
        sameSite: 'lax',
        secure: true,
        path: '/',
    });
}

/**
 * Persists an Auth0-issued access token in the shared `accessToken` cookie and
 * flags it as Auth0-issued.
 *
 * The `x-petm-auth0-enabled` request marker must only be advertised when the
 * bearer token we send is actually an Auth0 token. Since the legacy `accessToken`
 * cookie can still hold a Janrain token until Auth0 sync succeeds, this marker
 * cookie is the source of truth for "the access token is Auth0", not the
 * `auth0` feature flag on its own.
 *
 * Clears Janrain refresh metadata so `evaluateSession` does not call the legacy
 * `/api/acp/auth/refresh-token` endpoint while Auth0 owns token renewal.
 */
export function setAuth0AccessToken(accessToken: string) {
    const cookieOptions = { path: '/' };

    setAccessToken(accessToken);
    Cookies.set(AUTH_COOKIES.IS_ACCESS_TOKEN_AUTH0, 'true', {
        sameSite: 'lax',
        secure: true,
        path: '/',
    });
    Cookies.remove(AUTH_COOKIES.REFRESH_TOKEN, cookieOptions);
    Cookies.remove(AUTH_COOKIES.TOKEN_EXPIRY, cookieOptions);
    // `petmAnonymousKey` is deliberately preserved: it proves guest-cart ownership and
    // the post-login cart merge reads it after this runs (CIAM-787). Sign-out clears it.
}

export function isAccessTokenAuth0(): boolean {
    return Cookies.get(AUTH_COOKIES.IS_ACCESS_TOKEN_AUTH0) === 'true';
}

export function getRefreshToken() {
    return Cookies.get(AUTH_COOKIES.REFRESH_TOKEN) ?? '';
}

function clearAuth0CartMergeGuard() {
    if (typeof sessionStorage === 'undefined') {
        return;
    }
    sessionStorage.removeItem(AUTH0_CART_MERGE_COMPLETED_KEY);
}

export function removeAuthTokens() {
    invalidateAuthSessionEpoch();
    const cookieOptions = { path: '/' };
    Cookies.remove(AUTH_COOKIES.DWSID_TOKEN, cookieOptions);
    Cookies.remove(AUTH_COOKIES.ACCESS_TOKEN, cookieOptions);
    Cookies.remove(AUTH_COOKIES.REFRESH_TOKEN, cookieOptions);
    Cookies.remove(AUTH_COOKIES.LAST_ACTIVE, cookieOptions);
    Cookies.remove(AUTH_COOKIES.AUTH0_REAUTH, cookieOptions);
    Cookies.remove(AUTH_COOKIES.TOKEN_EXPIRY, cookieOptions);
    Cookies.remove(AUTH_COOKIES.IS_ACCESS_TOKEN_AUTH0, cookieOptions);
    removeAnonymousIdToken();
    clearAuth0CartMergeGuard();
}

export function removeRegularAuthTokens() {
    invalidateAuthSessionEpoch();
    const cookieOptions = { path: '/' };
    Cookies.remove(AUTH_COOKIES.DWSID_TOKEN, cookieOptions);
    Cookies.remove(AUTH_COOKIES.ACCESS_TOKEN, cookieOptions);
    Cookies.remove(AUTH_COOKIES.REFRESH_TOKEN, cookieOptions);
    markLastActiveExpired();
    Cookies.remove(AUTH_COOKIES.TOKEN_EXPIRY, cookieOptions);
    Cookies.remove(AUTH_COOKIES.IS_ACCESS_TOKEN_AUTH0, cookieOptions);
    removeAnonymousIdToken();
    clearAuth0CartMergeGuard();
}

export function getAnonymousIdToken() {
    return Cookies.get(AUTH_COOKIES.PETM_ANONYMOUS_KEY) ?? '';
}

export function setAnonymousIdToken(anonId: string) {
    Cookies.set(AUTH_COOKIES.PETM_ANONYMOUS_KEY, anonId);
}

export function removeAnonymousIdToken() {
    Cookies.remove(AUTH_COOKIES.PETM_ANONYMOUS_KEY);
}

export function getCustomerKey() {
    return Cookies.get(AUTH_COOKIES.PETM_CUSTOMER_KEY) ?? '';
}

export function getCartIdToken() {
    return Cookies.get(COOKIES.CART_ID) ?? '';
}

export function setCartCookie(cartId: string) {
    Cookies.set(COOKIES.CART_ID, cartId);
}

export function removeCartCookie() {
    Cookies.remove(COOKIES.CART_ID);
}

export async function removeOrderGrooveToken() {
    Cookies.remove(OG_COOKIE, { path: '/' });
}

export function getVisitorId() {
    return Cookies.get(COOKIES.X_PETM_VISITOR_ID) ?? '';
}

export function getChatbotEnabled() {
    const decisionsCookie = Cookies.get(AUTH_HEADERS.X_PETM_DECISIONS) ?? '{}';
    return Boolean(JSON.parse(decisionsCookie).chat_poc);
}

export function getChatbotModel() {
    const decisionsCookie = Cookies.get(AUTH_HEADERS.X_PETM_DECISIONS) ?? '{}';
    return JSON.parse(decisionsCookie).chatbot_model;
}

export function getChatbotReasoning() {
    const decisionsCookie = Cookies.get(AUTH_HEADERS.X_PETM_DECISIONS) ?? '{}';
    return JSON.parse(decisionsCookie).chatbot_reasoning;
}

export function getRememberMeCookie() {
    return Cookies.get(AUTH_COOKIES.REMEMBER_ME) ?? '';
}

export function isRememberMeTrue() {
    return getRememberMeCookie() === 'true';
}

/**
 * tracks if a user has selected the "keep me signed in" checkbox
 *
 * only stores cookie for session, since no expiry is set
 */
export function setRememberMeCookie(value: boolean) {
    Cookies.set(AUTH_COOKIES.REMEMBER_ME, value.toString(), {
        sameSite: 'lax',
        secure: true,
        path: '/',
    });
}

export function getOktaAccessToken() {
    return Cookies.get(OKTA_COOKIES.OKTA_ACCESS_TOKEN);
}

export function getOktaIdToken() {
    return Cookies.get(OKTA_COOKIES.OKTA_ID_TOKEN);
}

export function getStoreCookie() {
    return Cookies.get(COOKIES.STORE_ID) ?? '';
}

export function getChannel() {
    return Cookies.get(COOKIES.X_PETM_CHANNEL) ?? '';
}
